"use client";

import {
  useCreatorLocale,
  useCreatorTranslations,
} from "@/i18n/creator/useCreatorTranslations";
import { formatUsd } from "../formatUsd";

/** What `getMySubmissions` says about the creator agreement's monthly cap. */
export type PayoutCapSummary = {
  monthlyCapUsd: number;
  /** `YYYY-MM`, the months the cap held this creator's earnings with the brand. */
  cappedMonths: string[];
};

const monthKey = (date: Date) => date.toISOString().slice(0, 7);

/**
 * The capped month worth telling the creator about: this one, while it is
 * still running and spend no longer adds to it, or last month, while its
 * payment is the one they are waiting on. An older cap is settled history the
 * figures already reflect, and a standing notice about it would only be noise.
 */
export function recentCappedMonth(
  cappedMonths: readonly string[],
  now: Date,
): { month: string; current: boolean } | null {
  const thisMonth = monthKey(now);
  if (cappedMonths.includes(thisMonth)) {
    return { month: thisMonth, current: true };
  }
  const lastMonth = monthKey(
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)),
  );
  return cappedMonths.includes(lastMonth)
    ? { month: lastMonth, current: false }
    : null;
}

/** The earnings card's line about the monthly cap, when there is one to say. */
export function usePayoutCapNotice(
  payoutCap: PayoutCapSummary | undefined,
  brand: string,
): string | undefined {
  const t = useCreatorTranslations("home");
  const locale = useCreatorLocale();
  if (!payoutCap) return undefined;
  const recent = recentCappedMonth(payoutCap.cappedMonths, new Date());
  if (!recent) return undefined;
  const month = new Intl.DateTimeFormat(locale, {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(`${recent.month}-01T00:00:00.000Z`));
  const values = { cap: formatUsd(payoutCap.monthlyCapUsd), brand, month };
  return recent.current
    ? t("earnings.capReachedThisMonth", values)
    : t("earnings.capReachedLastMonth", values);
}
